import { Model, Factory } from 'miragejs'
import type { ModelDefinition } from 'miragejs/-types'
import { randBoolean, randNumber, randPhrase } from '@ngneat/falso'

import type { MockServer, MirageServer } from './server.js'
import { createErrorResponse } from './utils.js'

interface ReminderCreationRuleModel {
  name: string
  trigger_in: number
  trigger_type: number
  expiry_days: number
  apply_type: number
  active: boolean
  recurring_type: number | null
  recurring_interval: number | null
  recurring_times: number | null
  conditions: Array<{
    id?: number
    condition: number
    comparison: number
    value: string[]
    value_labels?: Array<{ value: string | number; label: string }>
  }>
  creation_rule_timings: Array<{
    id?: number
    send_method: number
    template: string
    days_to_deadline: number
    is_before: boolean
  }>
}

export const model = Model as ModelDefinition<ReminderCreationRuleModel>

export const factory = Factory.extend<ReminderCreationRuleModel>({
  name() {
    return randPhrase({ maxCharCount: 30 })
  },
  trigger_in() {
    return randNumber({ min: 0, max: 2 })
  },
  trigger_type() {
    return randNumber({ min: 1, max: 2 })
  },
  expiry_days() {
    return randNumber({ min: 1, max: 365 })
  },
  apply_type() {
    return randNumber({ min: 1, max: 2 })
  },
  active() {
    return randBoolean()
  },
  recurring_type() {
    return null
  },
  recurring_interval() {
    return null
  },
  recurring_times() {
    return null
  },
  conditions() {
    return []
  },
  creation_rule_timings() {
    return [
      {
        id: 1,
        send_method: 0,
        template: '1',
        days_to_deadline: 7,
        is_before: true,
      },
    ]
  },
})

// OPTIONS data formatted for the frontend normalizer
// Frontend expects: response.data.actions.POST.<field>.choices
const optionsData = {
  actions: {
    POST: {
      trigger_in: {
        choices: [
          { value: 0, display_name: 'Consultations and counter sales' },
          { value: 1, display_name: 'Consultations' },
          { value: 2, display_name: 'Counter sales' },
          { value: 3, display_name: 'Patient is created' },
        ],
      },
      trigger_type: {
        choices: [
          { value: 1, display_name: 'Item is added' },
          { value: 2, display_name: 'Invoice is finalized' },
        ],
      },
      apply_type: {
        choices: [
          { value: 1, display_name: 'All of the following conditions are met' },
          { value: 2, display_name: 'Any of the following conditions are met' },
        ],
      },
      recurring_type: {
        choices: [
          { value: 1, display_name: 'Daily' },
          { value: 2, display_name: 'Weekly' },
          { value: 3, display_name: 'Monthly' },
          { value: 4, display_name: 'Yearly' },
        ],
      },
      conditions: {
        child: {
          children: {
            condition: {
              choices: [
                { value: 1, display_name: 'Item' },
                { value: 2, display_name: 'Patient species' },
                { value: 3, display_name: 'Patient age in months' },
                { value: 4, display_name: 'Consultation diagnosis' },
                { value: 5, display_name: 'Amount of consultations' },
              ],
            },
            comparison: {
              choices: [
                { value: 1, display_name: 'In' },
                { value: 2, display_name: 'Not in' },
                { value: 3, display_name: 'Equal to' },
                { value: 4, display_name: 'Not equal to' },
                { value: 5, display_name: 'Greater than' },
                { value: 6, display_name: 'Greater than or equal to' },
                { value: 7, display_name: 'Less than' },
                { value: 8, display_name: 'Less than or equal to' },
                { value: 9, display_name: 'Between' },
              ],
            },
          },
        },
      },
      creation_rule_timings: {
        child: {
          children: {
            send_method: {
              choices: [
                { value: 0, display_name: 'Email' },
                { value: 1, display_name: 'SMS' },
                { value: 2, display_name: 'Email and SMS' },
              ],
            },
          },
        },
      },
    },
  },
}

export function routes(server: MockServer) {
  server.get('/reminder_creation_rules', function (this: MirageServer, schema) {
    const rules = schema.all('reminderCreationRule')
    const response = this.serialize(rules)

    return {
      ...response,
      meta: {
        pagination: {
          page: 1,
          pages: 1,
          count: rules.length,
        },
      },
    }
  })

  server.get(
    '/reminder_creation_rules/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const rule = schema.find('reminderCreationRule', id)

      if (!rule) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Creation rule with id ${id} not found` }],
        })
      }

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: [],
              meta: { count: 0 },
            },
            overridden_rules: {
              data: [],
              meta: { count: 0 },
            },
          },
        },
      }
    },
  )

  server.options('/reminder_creation_rules', function () {
    return {
      data: optionsData,
    }
  })

  server.patch(
    '/reminder_creation_rules/:id',
    function (this: MirageServer, schema, request) {
      const id = request.params.id
      const attrs = JSON.parse(request.requestBody)
      const rule = schema.find('reminderCreationRule', id)

      if (!rule) {
        return createErrorResponse({
          status: 404,
          errors: [{ detail: `Creation rule with id ${id} not found` }],
        })
      }

      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      rule.update(attrs.data.attributes)

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: [],
              meta: { count: 0 },
            },
            overridden_rules: {
              data: [],
              meta: { count: 0 },
            },
          },
        },
      }
    },
  )

  server.post(
    '/reminder_creation_rules',
    function (this: MirageServer, schema, request) {
      const attrs = JSON.parse(request.requestBody)

      if (!attrs?.data?.attributes) {
        return createErrorResponse({
          status: 400,
          errors: [{ detail: 'Missing "attributes" in request body' }],
        })
      }

      const rule = schema.create('reminderCreationRule', attrs.data.attributes)

      return {
        data: {
          type: 'CreationRule',
          id: rule.id,
          attributes: rule.attrs,
          relationships: {
            department: {
              data: { type: 'Department', id: '1' },
            },
            overridden_by: {
              data: [],
              meta: { count: 0 },
            },
            overridden_rules: {
              data: [],
              meta: { count: 0 },
            },
          },
        },
      }
    },
  )
}

export function seeds(server: MockServer) {
  server.create('reminderCreationRule', {
    name: 'Annual Health Check',
    trigger_in: 1,
    trigger_type: 1,
    expiry_days: 30,
    apply_type: 1,
    active: true,
    conditions: [],
    creation_rule_timings: [
      {
        id: 1,
        send_method: 0,
        template: '1',
        days_to_deadline: 7,
        is_before: true,
      },
    ],
  })

  server.create('reminderCreationRule', {
    name: 'Vaccination Reminder',
    trigger_in: 1,
    trigger_type: 1,
    expiry_days: 14,
    apply_type: 1,
    active: true,
    conditions: [],
    creation_rule_timings: [
      {
        id: 2,
        send_method: 1,
        template: '2',
        days_to_deadline: 3,
        is_before: true,
      },
    ],
  })
}
